/**
 * The footer on the booking page.
 *
 * It matters more here than a footer usually does. This page is often reached
 * from a QR code on a table tent or a WhatsApp link, by someone who has never
 * seen the restaurant's website — so it is the only place they can find a way
 * to reach a human when the booking they wanted is not available.
 *
 * Two sources, deliberately separated:
 *
 * - **Group-level** contacts are the constants below. One reservations line
 *   serves both branches, which is how the restaurant actually operates, so
 *   modelling them per venue would invent a distinction that does not exist.
 *
 * - **The address** comes from the API, because that genuinely differs per
 *   branch. A guest booking The Pearl must not be shown the other one's street.
 */

import { useTranslation } from 'react-i18next';
import type { Locale } from '@yp/shared';
import type { VenueDetail } from '../lib/api.js';

/**
 * From the restaurant. Two reservation lines and a separate WhatsApp number.
 *
 * `tel` is digits and a leading plus only — no spaces or slashes — because it
 * goes straight into a `tel:` href. `label` is the human form. Keeping them as
 * separate fields avoids the usual bug where a number formatted for reading is
 * dialled verbatim and fails.
 */
const PHONES = [
  { tel: '+97444111502', label: '+974 4411 1502' },
  { tel: '+97444111503', label: '+974 4411 1503' },
] as const;

/** wa.me wants digits with no plus and no separators. */
const WHATSAPP = { number: '97477111504', label: '+974 7711 1504' } as const;

const EMAIL = 'reservations@yasminepalace.com';
const WEBSITE = 'https://yasminepalace.com/';

const SOCIAL = [
  { key: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/yasminepalace/' },
  { key: 'facebook', label: 'Facebook', url: 'https://www.facebook.com/YasminePalaceQatar/' },
  { key: 'snapchat', label: 'Snapchat', url: 'https://www.snapchat.com/add/yasminepalaceqatar' },
] as const;

export function Footer({ venue, locale }: { venue: VenueDetail | null; locale: Locale }) {
  const { t } = useTranslation();
  const address = venue ? venue.address[locale] : null;

  return (
    <footer className="mt-2 border-t border-brand-gold/40 pt-6 text-center text-sm text-brand-ink-soft">
      {venue ? (
        <p className="font-display text-base text-brand-ink">{venue.name[locale]}</p>
      ) : null}
      {address ? <p className="mt-1">{address}</p> : null}

      <dl className="mt-4 space-y-2">
        <div>
          <dt className="text-xs uppercase tracking-wider text-brand-bronze">
            {t('footer.phone')}
          </dt>
          <dd className="mt-0.5 flex flex-wrap items-center justify-center gap-x-2">
            {PHONES.map((p, i) => (
              <span key={p.tel} className="flex items-center gap-2">
                {i > 0 ? <span aria-hidden="true">/</span> : null}
                {/* dir="ltr" is not decoration. A phone number inside Arabic
                    text is bidirectional, and without it the leading + can be
                    reordered to the wrong end of the number on screen. */}
                <a
                  href={`tel:${p.tel}`}
                  dir="ltr"
                  className="font-medium text-brand-teal hover:text-brand-teal-dark"
                >
                  {p.label}
                </a>
              </span>
            ))}
          </dd>
        </div>

        <div>
          <dt className="text-xs uppercase tracking-wider text-brand-bronze">
            {t('footer.whatsapp')}
          </dt>
          <dd className="mt-0.5">
            <a
              href={`https://wa.me/${WHATSAPP.number}`}
              target="_blank"
              rel="noopener noreferrer"
              dir="ltr"
              className="font-medium text-brand-teal hover:text-brand-teal-dark"
            >
              {WHATSAPP.label}
            </a>
          </dd>
        </div>

        <div>
          <dt className="text-xs uppercase tracking-wider text-brand-bronze">
            {t('footer.email')}
          </dt>
          <dd className="mt-0.5">
            <a
              href={`mailto:${EMAIL}`}
              dir="ltr"
              className="font-medium text-brand-teal hover:text-brand-teal-dark"
            >
              {EMAIL}
            </a>
          </dd>
        </div>
      </dl>

      <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {SOCIAL.map((s) => (
          <li key={s.key}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-bronze hover:text-brand-bronze-dark"
            >
              {s.label}
            </a>
          </li>
        ))}
        <li>
          <a
            href={WEBSITE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-bronze hover:text-brand-bronze-dark"
          >
            {t('footer.website')}
          </a>
        </li>
      </ul>

      <p className="mt-5 text-xs text-brand-ink-soft/80">{t('footer.rights')}</p>
    </footer>
  );
}
